// Tiny event bus behind the portal's feedback API, so showToast() and
// confirmDialog() work from anywhere (components, thunks, plain helpers)
// once <FeedbackProvider> is mounted in App.js. Requests made before the
// host mounts are kept (at most 5) and shown when it does.

const MAX_PENDING = 5;

const createChannel = () => {
  const listeners = new Set();
  let pending = [];
  return {
    subscribe(fn) {
      listeners.add(fn);
      if (pending.length) {
        const queued = pending;
        pending = [];
        queued.forEach((evt) => fn(evt));
      }
      return () => listeners.delete(fn);
    },
    emit(evt) {
      if (listeners.size === 0) {
        pending = pending.concat([evt]).slice(-MAX_PENDING);
        return;
      }
      listeners.forEach((fn) => fn(evt));
    },
  };
};

export const toastChannel = createChannel();
export const confirmChannel = createChannel();

export const TOAST_KINDS = ['success', 'error', 'info', 'warning'];

let nextToastId = 1;

/**
 * Show a short message at the bottom of the screen (no OK to tap).
 *
 *   showToast({ message, kind, actionLabel, onAction, duration, id })
 *   showToast('Saved')                       // kind 'info'
 *   showToast.success(t('saved')) / .error(msg, { actionLabel, onAction }) / .info / .warning
 *
 * kind: 'success' | 'error' | 'info' | 'warning' (default 'info').
 * duration (ms): default success 3000, info 4000, warning and error 5000,
 *   at least 6000 when there is an action; null keeps it until dismissed
 *   (it then gets a Close button).
 * id: pass the same id again to replace a toast in place (for example an
 *   "Offline" toast), and dismissToast(id) to remove it.
 * Returns the toast id.
 */
export function showToast(arg) {
  const opts = typeof arg === 'string' ? { message: arg } : (arg || {});
  if (opts.message === undefined || opts.message === null || opts.message === '') return null;
  const id = opts.id || `toast-${nextToastId++}`;
  const kind = TOAST_KINDS.indexOf(opts.kind) >= 0 ? opts.kind : 'info';
  toastChannel.emit({ type: 'show', toast: { ...opts, id, kind } });
  return id;
}

TOAST_KINDS.forEach((kind) => {
  showToast[kind] = (message, opts) => showToast({ ...(opts || {}), message, kind });
});

export const dismissToast = (id) => {
  toastChannel.emit({ type: 'dismiss', id });
};

/**
 * Ask before doing something that cannot be undone. Resolves true when the
 * person confirms, false when they cancel or close the dialog.
 *
 *   const ok = await confirmDialog({
 *     title: 'Delete driver Abebe K.?',      // a specific question
 *     body: 'Their trips stay in the reports.',
 *     confirmLabel: 'Delete',                // the verb, never "OK"
 *     cancelLabel: t('cancel'),              // default t('cancel'); null = one-button notice
 *     destructive: true,                     // red confirm, focus starts on Cancel
 *   });
 */
export function confirmDialog(opts) {
  return new Promise((resolve) => {
    confirmChannel.emit({ ...(opts || {}), resolve });
  });
}
